import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import IconButton from "@mui/material/IconButton";
import RemoveIcon from "@mui/icons-material/Remove";
import { Button, CircularProgress, InputBase, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

const LINE = "#E2E8F0";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const SUCCESS = "#2E7D32";
const SUCCESS_BG = "#E8F5E9";
const BODY_FONT = '"Source Sans 3", sans-serif';
const CONTROL_HEIGHT = 24;
const SUCCESS_HOLD_MS = 900;

type AddFeedback = "idle" | "loading" | "success";

interface IShoppingCartItemProps {
  rowParams: any;
  updateCartLoading: boolean;
  handleAvailableCart: (packageAvailable: number) => void | Promise<void>;
  /** When true, shows a labeled "Add to cart" button instead of the cart icon. */
  useAddToCartButton?: boolean;
}

function clampQty(n: number, max: number): number {
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, max);
}

export const BuyAction: React.FunctionComponent<IShoppingCartItemProps> = ({
  rowParams,
  updateCartLoading,
  handleAvailableCart,
  useAddToCartButton = false,
}) => {
  const max = rowParams?.row?.available ?? 1;
  const rowKey = rowParams?.id ?? rowParams?.row?.id;
  const [packageAvailable, setPackageAvailable] = useState<number>(1);
  const [addFeedback, setAddFeedback] = useState<AddFeedback>("idle");
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setPackageAvailable(1);
    setAddFeedback("idle");
    if (successTimer.current) {
      clearTimeout(successTimer.current);
      successTimer.current = null;
    }
  }, [rowKey, max]);

  useEffect(() => {
    return () => {
      if (successTimer.current) {
        clearTimeout(successTimer.current);
      }
    };
  }, []);

  const handleChangePackageAvailable = (isPlus: boolean = true) => {
    setPackageAvailable((prev) => {
      const next = isPlus ? prev + 1 : prev - 1;
      return clampQty(next, max);
    });
  };

  const commitQtyFromInput = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (digits === "") {
      setPackageAvailable(1);
      return;
    }
    setPackageAvailable(clampQty(parseInt(digits, 10), max));
  };

  const busy = updateCartLoading || addFeedback === "loading";

  const onAddToCart = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (busy || addFeedback === "success" || max < 1) return;

    setAddFeedback("loading");
    try {
      await Promise.resolve(handleAvailableCart(packageAvailable));
      setAddFeedback("success");
      if (successTimer.current) clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => {
        setAddFeedback("idle");
        successTimer.current = null;
      }, SUCCESS_HOLD_MS);
    } catch {
      setAddFeedback("idle");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        height: "100%",
        flexWrap: "nowrap",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: 86,
          minWidth: 86,
          flexShrink: 0,
          height: CONTROL_HEIGHT,
          border: `1px solid ${LINE}`,
          borderRadius: "8px",
          bgcolor: "#FFFFFF",
          px: "2px",
          boxSizing: "border-box",
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <IconButton
          size="small"
          sx={{ p: 0.25, color: PRIMARY }}
          onClick={() => handleChangePackageAvailable(false)}
          disabled={packageAvailable <= 1 || busy}
        >
          <RemoveIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Divider sx={{ height: 14, mx: 0.25, borderColor: LINE_SOFT }} orientation="vertical" />
        <InputBase
          sx={{
            flex: 1,
            minWidth: 0,
            input: {
              textAlign: "center",
              padding: 0,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: BODY_FONT,
              lineHeight: 1,
            },
          }}
          value={packageAvailable}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            "aria-label": "Quantity to add",
          }}
          onChange={(e) => commitQtyFromInput(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
        <Divider sx={{ height: 14, mx: 0.25, borderColor: LINE_SOFT }} orientation="vertical" />
        <IconButton
          size="small"
          sx={{ p: 0.25, color: PRIMARY }}
          onClick={() => handleChangePackageAvailable()}
          disabled={packageAvailable >= max || busy}
        >
          <AddIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
      {useAddToCartButton ? (
        <Tooltip title="Add to Cart" arrow placement="top">
          <span>
            <Button
              variant="contained"
              size="small"
              aria-label="Add to Cart"
              disabled={busy || max < 1 || addFeedback === "success"}
              onClick={onAddToCart}
              sx={{
                height: CONTROL_HEIGHT,
                minWidth: CONTROL_HEIGHT + 8,
                flexShrink: 0,
                bgcolor: addFeedback === "success" ? SUCCESS : PRIMARY,
                color: "#FFFFFF",
                borderRadius: "8px",
                fontWeight: 700,
                fontFamily: BODY_FONT,
                fontSize: 14,
                lineHeight: 1,
                textTransform: "none",
                boxShadow: "none",
                px: 1,
                whiteSpace: "nowrap",
                gap: 0.25,
                "&:hover": {
                  bgcolor: addFeedback === "success" ? SUCCESS : PRIMARY_DARK,
                  boxShadow: "none",
                },
              }}
            >
              {addFeedback === "loading" ? (
                <CircularProgress color="inherit" size={12} />
              ) : addFeedback === "success" ? (
                <CheckRoundedIcon sx={{ fontSize: 16 }} />
              ) : (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                  <Box component="span" sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1 }}>
                    +
                  </Box>
                  <ShoppingCartOutlinedIcon sx={{ fontSize: 15 }} />
                </Box>
              )}
            </Button>
          </span>
        </Tooltip>
      ) : (
        <IconButton
          size="small"
          aria-label={addFeedback === "success" ? "Added to cart" : "Add to cart"}
          disabled={busy || addFeedback === "success"}
          onClick={onAddToCart}
          sx={{
            width: CONTROL_HEIGHT,
            height: CONTROL_HEIGHT,
            color: addFeedback === "success" ? SUCCESS : PRIMARY,
            border: `1px solid ${addFeedback === "success" ? SUCCESS : LINE}`,
            borderRadius: "8px",
            bgcolor: addFeedback === "success" ? SUCCESS_BG : "#FFFFFF",
            p: 0,
            transition: "background-color .15s ease, border-color .15s ease, color .15s ease",
            "&:hover": {
              color: addFeedback === "success" ? SUCCESS : PRIMARY,
              bgcolor: addFeedback === "success" ? SUCCESS_BG : "#F1F5F9",
              borderColor: addFeedback === "success" ? SUCCESS : PRIMARY,
            },
          }}
        >
          {addFeedback === "loading" ? (
            <CircularProgress size={14} thickness={5} sx={{ color: PRIMARY }} />
          ) : addFeedback === "success" ? (
            <CheckRoundedIcon sx={{ fontSize: 18 }} />
          ) : (
            <ShoppingCartOutlinedIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      )}
    </Box>
  );
};

export default BuyAction;
